import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { visitoriaService } from '@/services/vistoria'
import type { VisitoriaFormData } from '@/types'
import { FileText, Upload, Save, ArrowLeft, Navigation, RefreshCw } from 'lucide-react'
import { useGeolocation } from '@/hooks/useGeolocation'

export function VisitoriaFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    referencia: '',
    descricao: '',
    latitude: 0,
    longitude: 0,
    accuracy: 0
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Hook de geolocalização
  const { 
    position, 
    loading: geoLoading, 
    getCurrentPosition, 
    getCurrentPositionHighAccuracy, 
    getCurrentPositionFast,
    accuracy,
    error: geoError
  } = useGeolocation()

  // Verificar se o navegador suporta geolocalização
  const isGeolocationSupported = navigator.geolocation

  // Função para verificar o status da permissão
  const checkGeolocationPermission = async () => {
    if (!navigator.permissions) {
      console.log('⚠️ Permissions API não suportada')
      return 'unknown'
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      console.log('🔍 Status da permissão:', permission.state)
      return permission.state
    } catch (error) {
      console.log('⚠️ Erro ao verificar permissão:', error)
      return 'unknown'
    }
  }

  const loadVistoria = async () => {
    if (!id) return

    try {
      setLoading(true)
      const response = await visitoriaService.getById(parseInt(id))
      
      if (response.success && response.data) {
        setFormData({
          referencia: response.data.referencia,
          descricao: response.data.descricao || '',
          latitude: Number(response.data.latitude) || 0,
          longitude: Number(response.data.longitude) || 0,
          accuracy: Number(response.data.accuracy) || 0
        })
      } else {
        toast.error('Vistoria não encontrada')
        navigate('/vistorias')
      }
    } catch (error) {
      console.error('Erro ao carregar vistoria:', error)
      toast.error('Erro ao carregar vistoria')
      navigate('/vistorias')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleGetCurrentLocation = async (mode: 'normal' | 'high' | 'fast' = 'normal') => {
    try {
      // Verificar se o navegador suporta geolocalização
      if (!navigator.geolocation) {
        toast.error('Seu navegador não suporta geolocalização')
        return
      }

      // Verificar se estamos em HTTPS (necessário para geolocalização em produção)
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        toast.error('Geolocalização requer HTTPS em produção. Use HTTP apenas em localhost.')
        return
      }

      console.log('🔍 Iniciando solicitação de geolocalização...')
      console.log('📍 Protocolo:', location.protocol)
      console.log('🌐 Hostname:', location.hostname)
      console.log('📱 User Agent:', navigator.userAgent)

      // Verificar status da permissão
      const permissionStatus = await checkGeolocationPermission()
      console.log('🔐 Status da permissão:', permissionStatus)

      if (permissionStatus === 'denied') {
        toast.error('Permissão de localização foi negada. Clique no ícone de localização na barra de endereços para permitir.')
        return
      }

      // Mostrar mensagem informativa antes de solicitar permissão
      toast.loading('Solicitando permissão para acessar sua localização...', { duration: 2000 })

      let position
      
      switch (mode) {
        case 'high':
          console.log('🎯 Modo: Alta Precisão')
          position = await getCurrentPositionHighAccuracy()
          toast.success('Localização de alta precisão obtida!')
          break
        case 'fast':
          console.log('⚡ Modo: Rápido')
          position = await getCurrentPositionFast()
          toast.success('Localização rápida obtida!')
          break
        default:
          console.log('📍 Modo: Normal')
          position = await getCurrentPosition()
          toast.success('Localização obtida com sucesso!')
      }
      
      console.log('✅ Posição obtida:', position)
      
      if (position?.coords) {
        setFormData(prev => ({
          ...prev,
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          accuracy: Number(position.coords.accuracy.toFixed(3))
        }))
      }
      
      return position
    } catch (error: any) {
      console.error('❌ Erro de geolocalização:', error)
      console.error('❌ Código do erro:', error.code)
      console.error('❌ Mensagem do erro:', error.message)
      
      // Tratamento específico para diferentes tipos de erro
      if (error.code === 1 || error.message.includes('PERMISSION_DENIED') || error.message.includes('Permissão para acessar localização foi negada')) {
        toast.error(
          'Permissão negada. Clique no ícone de localização na barra de endereços para permitir o acesso.',
          { duration: 6000 }
        )
      } else if (error.code === 2 || error.message.includes('POSITION_UNAVAILABLE')) {
        toast.error(
          'Localização indisponível. Verifique se o GPS está ativado e tente novamente.',
          { duration: 5000 }
        )
      } else if (error.code === 3 || error.message.includes('TIMEOUT')) {
        toast.error(
          'Tempo limite excedido. Tente novamente.',
          { duration: 5000 }
        )
      } else {
        toast.error(
          `Erro inesperado: ${error.message || 'Erro desconhecido'}`,
          { duration: 5000 }
        )
      }
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validar tipo de arquivo (PDF, DOC, DOCX, imagens)
      const allowedTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg'
      ]
      if (!allowedTypes.includes(file.type)) {
        toast.error('Apenas arquivos PDF, DOC, DOCX ou imagens (JPG, PNG) são permitidos')
        return
      }
      
      // Validar tamanho (máximo 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo permitido: 50MB')
        return
      }
      
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    console.log('Dados do formulário:', formData)

    if (!formData.referencia.trim()) {
      toast.error('Referência da vistoria é obrigatória')
      return
    }

    if (formData.latitude === 0 || formData.longitude === 0) {
      toast.error('Latitude e longitude são obrigatórias')
      return
    }

    try {
      setSubmitting(true)

      if (isEdit) {
        // Atualizar vistoria existente
        const updateData: Partial<VisitoriaFormData> = {
          referencia: formData.referencia,
          descricao: formData.descricao,
          latitude: formData.latitude,
          longitude: formData.longitude,
          accuracy: formData.accuracy
        }

        console.log('Dados para atualização:', updateData)
        const response = await visitoriaService.update(parseInt(id!), updateData)
        console.log('Resposta da atualização:', response)
        
        if (response.success) {
          toast.success('Vistoria atualizada com sucesso!')
          navigate('/vistorias')
        } else {
          toast.error(response.message || 'Erro ao atualizar vistoria')
        }
      } else {
        // Criar nova vistoria
        const createData: VisitoriaFormData = {
          referencia: formData.referencia,
          descricao: formData.descricao,
          latitude: formData.latitude,
          longitude: formData.longitude,
          accuracy: formData.accuracy
        }

        console.log('Dados para criação:', createData)
        const response = await visitoriaService.create(createData, selectedFile || undefined)
        console.log('Resposta da criação:', response)
        
        if (response.success) {
          toast.success('Vistoria criada com sucesso!')
          navigate('/vistorias')
        } else {
          toast.error(response.message || 'Erro ao criar vistoria')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar:', error)
      toast.error('Erro ao salvar vistoria')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (isEdit) {
      loadVistoria()
    }
  }, [id])

  if (loading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-2 text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/vistorias')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
        
        <div className="flex items-center space-x-4">
          <FileText className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? 'Editar Vistoria' : 'Nova Vistoria'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Atualizar informações da vistoria' : 'Adicionar nova vistoria'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Referência da Vistoria */}
            <div>
              <label htmlFor="referencia" className="block text-sm font-medium text-gray-700 mb-2">
                Referência da Vistoria *
              </label>
              <input
                type="text"
                id="referencia"
                value={formData.referencia}
                onChange={(e) => handleInputChange('referencia', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Ex: Vistoria de Equipamentos de Segurança - Janeiro 2024"
                required
              />
            </div>

            {/* Descrição da Vistoria */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição da Vistoria
              </label>
              <textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Descreva detalhes adicionais sobre a vistoria..."
                rows={4}
              />
            </div>

            {/* Coordenadas */}
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-gray-900">Coordenadas</h3>
                {isGeolocationSupported ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('normal')}
                      loading={geoLoading}
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Obter Localização
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('high')}
                      loading={geoLoading}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Alta Precisão
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('fast')}
                      loading={geoLoading}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rápido
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://www.google.com/maps', '_blank')}
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      title="Abrir Google Maps para obter coordenadas"
                    >
                      🗺️ Google Maps
                    </Button>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                    <p><strong>⚠️ Geolocalização não suportada</strong></p>
                    <p>Seu navegador não suporta geolocalização. Preencha as coordenadas manualmente ou use o Google Maps.</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://www.google.com/maps', '_blank')}
                      className="mt-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      🗺️ Abrir Google Maps
                    </Button>
                  </div>
                )}
                
                {geoError && (
                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                    {geoError}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-2">
                    Latitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="latitude"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="-23.550520"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-2">
                    Longitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="longitude"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="-46.633308"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="accuracy" className="block text-sm font-medium text-gray-700 mb-2">
                    Precisão (metros)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    id="accuracy"
                    value={formData.accuracy}
                    onChange={(e) => handleInputChange('accuracy', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="10.000"
                  />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                <p><strong>Modos de localização:</strong></p>
                <ul className="mt-1 space-y-1">
                  <li>• <strong>Normal:</strong> Balanceado entre velocidade e precisão</li>
                  <li>• <strong>Alta Precisão:</strong> Usa GPS para maior exatidão (pode demorar mais)</li>
                  <li>• <strong>Rápido:</strong> Localização aproximada usando rede WiFi/celular</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p><strong>💡 Como permitir localização:</strong></p>
                <ul className="mt-1 space-y-1">
                  <li>• <strong>1º:</strong> Clique em "Obter Localização" - o navegador perguntará permissão</li>
                  <li>• <strong>2º:</strong> Clique em "Permitir" quando aparecer a solicitação</li>
                  <li>• <strong>3º:</strong> Se negou antes, clique no ícone 🔒 na barra de endereços</li>
                  <li>• <strong>Alternativa:</strong> Use o Google Maps ou preencha manualmente</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                <p><strong>🔧 Diagnóstico:</strong></p>
                <ul className="mt-1 space-y-1">
                  <li>• <strong>Protocolo:</strong> {location.protocol}</li>
                  <li>• <strong>Hostname:</strong> {location.hostname}</li>
                  <li>• <strong>Geolocalização:</strong> {isGeolocationSupported ? '✅ Suportada' : '❌ Não suportada'}</li>
                  <li>• <strong>HTTPS:</strong> {location.protocol === 'https:' ? '✅ Sim' : '❌ Não (necessário em produção)'}</li>
                </ul>
                <p className="mt-2 text-xs text-gray-500">
                  💡 Abra o console (F12) para ver logs detalhados da geolocalização
                </p>
              </div>
            </div>

            {/* Upload de Arquivo (Opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arquivo Anexo (Opcional)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
                    >
                      <span>Selecionar arquivo</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">ou arraste e solte</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, DOCX, JPG, PNG até 50MB
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-green-600 font-medium">
                      Arquivo selecionado: {selectedFile.name}
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Anexe fotos, documentos ou relatórios relacionados à vistoria
              </p>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/vistorias')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Atualizar' : 'Salvar'} Vistoria
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
